import { useEffect, useState } from 'react';
import { getMarchHttpApiBase, isLikelyS3StaticUiHostname, isMarchApiSameOrigin } from '../api/marchApiEnv';

/**
 * Keep deployed static UI on the origin that matches its API transport.
 * Current AWS ALBs are HTTP-only, so opening the UI from HTTPS S3 REST or
 * CloudFront causes browser mixed-content blocking for `/health` and API calls.
 */
export function SameOriginApiOriginBanner() {
  const [message, setMessage] = useState('');

  const canonical = String(import.meta.env.VITE_CANONICAL_UI_ORIGIN ?? '')
    .trim()
    .replace(/\/+$/, '');

  useEffect(() => {
    const apiBase = getMarchHttpApiBase();
    const apiIsHttpOnly = apiBase.toLowerCase().startsWith('http://');
    const openedOverHttps = window.location.protocol === 'https:';

    if (apiIsHttpOnly && openedOverHttps && canonical.toLowerCase().startsWith('http://')) {
      window.location.replace(`${canonical}${window.location.pathname}${window.location.search}${window.location.hash}`);
      return;
    }

    if (isMarchApiSameOrigin() && isLikelyS3StaticUiHostname(window.location.hostname)) {
      setMessage('This page is loaded from S3; /health and /api are not proxied here, so the console stays API Offline.');
      return;
    }

    if (apiIsHttpOnly && openedOverHttps) {
      setMessage('This page is loaded over HTTPS but the current environment API is HTTP-only, so the browser blocks API calls.');
    }
  }, [canonical]);

  if (!message) return null;

  return (
    <div
      role="status"
      className="sticky top-0 z-100 shrink-0 border-b border-(--status-attention)/35 bg-status-attention-bg px-3 py-2 text-center text-[11px] leading-snug text-status-attention-text sm:text-[12px]"
    >
      <strong className="font-medium">API routing:</strong>{' '}
      {message}
      {canonical ? (
        <>
          {' '}
          <a
            href={canonical}
            className="font-medium text-status-attention-text underline underline-offset-2 hover:no-underline"
          >
            Open correct UI - {canonical.replace(/^https?:\/\//i, '')}
          </a>
        </>
      ) : (
        <> Ask your team for the canonical UI URL for this environment.</>
      )}
    </div>
  );
}
